import { html, nothing, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";
import {
    SubmissionStatus,
    submissionStatusColor,
    submissionStatusIcon,
    submissionStatusLabel,
} from "@/submission_status";

/**
 * Bar on top of the feedback shown in the hand-in card.
 * Names the result of the submission being shown and repeats the edit action of the card
 * footer, which sits below feedback that can be arbitrarily long. d-handin-card sets
 * `submission` when it switches to a submission, and clears it when the editor returns.
 *
 * Styles: app/assets/stylesheets/components/handin.css.scss (d-submission-bar)
 *
 * @element d-submission-bar
 *
 * @prop {SubmissionStatus|null} submission - Submission being shown, or null while editing.
 * @prop {boolean} canEdit - Whether the submission can be copied into the editor.
 * @prop {boolean} showBack - Whether the editor can still be returned to as it is, which stops
 *   being true once something has been handed in.
 *
 * @fires edit - When the submission should be copied into the editor.
 * @fires back - When the editor should be shown as it is.
 */
@customElement("d-submission-bar")
export class SubmissionBar extends DodonaElement {
    @property({ type: Object })
    submission: SubmissionStatus | null = null;

    @property({ type: Boolean, attribute: "can-edit" })
    canEdit = false;

    @property({ type: Boolean, attribute: "show-back" })
    showBack = false;

    private dispatch(name: string): void {
        this.dispatchEvent(new CustomEvent(name, { bubbles: true }));
    }

    private action(event: string, className: string, icon: string, label: string): TemplateResult {
        return html`
            <button type="button" class="btn ${className} with-icon" @click=${() => this.dispatch(event)}>
                <i class="mdi mdi-${icon}" aria-hidden="true"></i>
                ${label}
            </button>
        `;
    }

    render(): TemplateResult {
        if (!this.submission) {
            return html``;
        }

        const color = submissionStatusColor(this.submission.status);
        const back = this.showBack
            ? this.action("back", "btn-outline", "arrow-left", I18n.t("js.handin.back_to_editor"))
            : nothing;
        const edit = this.canEdit
            ? this.action("edit", "btn-filled", "pencil", I18n.t("js.handin.edit_submission"))
            : nothing;

        return html`
            <span class="submission-bar-icon submission-bar-icon--${color}" aria-hidden="true">
                <i class="mdi mdi-18 mdi-${submissionStatusIcon(this.submission.status)}"></i>
            </span>

            <span class="submission-bar-status">
                <span class="submission-bar-label">${submissionStatusLabel(this.submission.status)}</span>
                ${
                    this.submission.summary
                        ? html`<span class="submission-bar-summary">${this.submission.summary}</span>`
                        : nothing
                }
                <d-time-ago class="submission-bar-time" datetime="${this.submission.created_at}" compact></d-time-ago>
            </span>

            ${
                this.showBack || this.canEdit
                    ? html`<span class="submission-bar-actions">${back}${edit}</span>`
                    : nothing
            }
        `;
    }
}
